import { useEffect, useRef } from 'react';
import { getFocusableElements } from '@/lib/a11y-utils';

/** Optional behaviour for {@link useFocusTrap}. */
export interface FocusTrapOptions {
  /**
   * The element to focus when the trap activates, instead of the first focusable element.
   * Read through `.current` when the deferred focus runs, so it may be attached after the
   * trap's effect. Ignored when it is null, detached, or outside the container.
   */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Traps keyboard focus within a container element when active.
 * Restores focus to the previously focused element when deactivated.
 *
 * ## Invariants:
 * - The trap owns the ONE deferred initial focus (the `requestAnimationFrame` below). A caller
 *   that wants a different element focused passes `initialFocusRef`; it must not schedule its own
 *   `setTimeout`/rAF focus, because the two then race and whichever runs last wins. That race is
 *   the cause of PB-8043: the SMS sheet's `setTimeout(0)` focused the number field, and the
 *   trap's rAF moved focus to the close button straight after, nondeterministically.
 * - Without `initialFocusRef`, or when its element is null, detached or outside the container,
 *   the first focusable element is focused, exactly as before the option existed.
 *
 * @param containerRef - Ref to the container element to trap focus within
 * @param active - Whether the focus trap is currently active
 * @param options - Optional; `initialFocusRef` names the element to focus on activation
 */
export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement | null>,
  active: boolean,
  options?: FocusTrapOptions
) {
  const previousFocusRef = useRef<HTMLElement | null>(null);
  // The ref object is stable across renders; depending on it (not on `options`) keeps the trap
  // from re-running on every render that passes a fresh options literal.
  const initialFocusRef = options?.initialFocusRef;

  useEffect(() => {
    if (!active || !containerRef.current) return;

    // Store the currently focused element to restore later
    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const container = containerRef.current;

    // Focus the requested element, else the first focusable element inside the container
    const focusableElements = getFocusableElements(container);
    if (focusableElements.length > 0) {
      // Small delay to ensure DOM is ready (portals, animations)
      requestAnimationFrame(() => {
        const requested = initialFocusRef?.current;
        if (requested && requested.isConnected && container.contains(requested)) {
          requested.focus();
          return;
        }
        const firstFocusable = getFocusableElements(container)[0];
        firstFocusable?.focus();
      });
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusable = getFocusableElements(container);
      if (focusable.length === 0) return;

      const firstElement = focusable[0];
      const lastElement = focusable[focusable.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: if focus is on first element, wrap to last
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: if focus is on last element, wrap to first
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);

      // Restore focus to the previously focused element (only if still in the DOM)
      if (
        previousFocusRef.current &&
        typeof previousFocusRef.current.focus === 'function' &&
        document.contains(previousFocusRef.current)
      ) {
        previousFocusRef.current.focus();
      }
    };
  }, [active, containerRef, initialFocusRef]);
}
